import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { $accountForIntegrationsStore, $accountTypesStore } from '@lib/store';
import type { AccountForIntegrationsRequest, AccountForIntegrationsResponse, IntegrationAccountType, IntegrationType } from '@shared/api';
import { IntegrationAccountTable } from './integrationAccountTable';

type CategorySelection = {
  [id: string]: string;
};

export interface IntegratedAccount extends AccountForIntegrationsResponse {
  editCategory?: string;
  integration: {
    category?: string;
    connected?: boolean;
  } | null;
}

const getActiveIntegration = (integration: IntegrationType, item: AccountForIntegrationsResponse) => {
  if (!item.integrations || !item.integrations.length) return null;

  const int = integration.toUpperCase();
  const result = item.integrations.filter((item) => item.serviceName?.toUpperCase() === int)[0];
  if (!result) return null;

  return { connected: result.connected, category: result.category };
};

interface AccountsProps {
  integration: IntegrationType;
  currency?: string;
  currentPage: number;
  countItemPage: number;
  onChangeCountItemPage: (count: number) => void;
  onChangePage: (index: number) => void;
}

export const Accounts = (props: AccountsProps) => {
  const accountStore = useUnit($accountForIntegrationsStore.store);
  const accountTypesStore = useUnit($accountTypesStore.store);

  const [editCategories, setEditCategories] = useState<CategorySelection>({});

  useEffect(() => {
    $accountTypesStore.events.getAccountTypes();
    return () => $accountTypesStore.events.clear();
  }, []);

  const params: AccountForIntegrationsRequest = useMemo(
    () => ({
      Count: props.countItemPage,
      FromIndex: (props.currentPage - 1) * props.countItemPage,
      ReturnTotalCount: true,
      Currency: props.currency,
    }),
    [props.countItemPage, props.currentPage, props.currency],
  );

  const clearEditCategory = useCallback(
    (accountId: number) => {
      setEditCategories((prev) => {
        const result = { ...prev };
        delete result[accountId];
        return result;
      });
    },
    [setEditCategories],
  );
  const handleCategoryChange = useCallback(
    ({ accountId, category }: { accountId: number; category: string }) => setEditCategories((prev) => ({ ...prev, [accountId]: category })),
    [setEditCategories],
  );
  const connectHandler = useCallback(
    async (row: IntegratedAccount) => {
      const category = (row.editCategory || row.integration?.category) as IntegrationAccountType;
      await $accountForIntegrationsStore.events.connectAccounts({ accountId: row.id, accountType: category });
      clearEditCategory(row.id);
      $accountForIntegrationsStore.events.getAccountsForIntegrations(params);
    },
    [clearEditCategory, $accountForIntegrationsStore, params],
  );
  const disconnectHandler = useCallback(
    async (row: IntegratedAccount) => {
      await $accountForIntegrationsStore.events.disconnectAccount({ accountId: row.id, service: props.integration });
      $accountForIntegrationsStore.events.getAccountsForIntegrations(params);
    },
    [$accountForIntegrationsStore, params, props.integration],
  );

  const items = useMemo<IntegratedAccount[]>(() => {
    return accountStore.data && accountStore.data.data
      ? accountStore.data.data.map((item) => ({
          ...item,
          integration: getActiveIntegration(props.integration, item),
          editCategory: editCategories[item.id],
        }))
      : [];
  }, [accountStore.data, props.integration, editCategories]);
  return (
    <>
      <IntegrationAccountTable
        isLoading={accountStore.loading}
        error={accountStore.error}
        accounts={items}
        categories={accountTypesStore.data?.accountTypes}
        currentPage={props.currentPage}
        totalItemCount={accountStore.data?.total || 0}
        countItemPage={props.countItemPage}
        onChangeCategory={handleCategoryChange}
        onConnect={connectHandler}
        onDisconnect={disconnectHandler}
        onChangeCountItemPage={props.onChangeCountItemPage}
        onChangePage={props.onChangePage}
        onRefresh={() => $accountForIntegrationsStore.events.getAccountsForIntegrations(params)}
      />
    </>
  );
};
